import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// 1) Importamos tu GenericTable “expandible”
import { GenericTable } from "@ui-components/Table/GenericTable";
// 2) Columnas base: prefix, family, etc.
import { BgpTables, tablesColumns } from "./BgpTablesData";

// 3) Servicios
import {
    getCTXRlistGlobal,
    getNeighborsListByCtxrId,
    postBgpSummary,
} from "./request/request";

import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";

import { ReactComponent as IconStar } from "@assets/Icons/Icon_StarOpen.svg";
import { ReactComponent as IconClosed } from "@assets/Icons/Icon_StarClosed.svg";

interface CtxrItem {
    id: string;
    name: string;
    description: string;
    subscriptionId: number;
    Suscription?: string;
}

interface BgpSummaryItem {
    nlri?: string;
    family?: string;
    neighbor?: string;
    localpreference?: string;
    community?: string;
    extendedcommunity?: string;
    type?: string;
    routerid?: string;
    aspath?: string;
    localip?: string;
    localas?: string;
    peeras?: string;
    subnet?: string;
    route_table?: string;
    // ...cualquier otro campo
}
/** Extendemos BgpTables para la subfila expandida */
interface ExtendedBgpTables extends BgpTables {
    routerId?: string;
    asPath?: string;
    localIp?: string;
    localAs?: string;
    peerAs?: string;
    subnet?: string;
    route_table?: string;
    // etc: label, origin, RD, etc.
}

export function BgpTablesPage() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

    const [isFetching, setIsFetching] = useState(false);
    // Usaremos ExtendedBgpTables (7 col + extra)
    const [tablesData, setTablesData] = useState<ExtendedBgpTables[]>([]);

    // Filtro local
    const [searchTerm, setSearchTerm] = useState("");
    const [searchColumn, setSearchColumn] = useState("All");

    // 1) CTX-R
    const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
    const [firstOptions, setFirstOptions] = useState<string[]>([]);
    const [selectedFirst, setSelectedFirst] = useState("");

    // 2) Neighbors
    const [secondOptions, setSecondOptions] = useState<string[]>([]);
    const [selectedSecond, setSelectedSecond] = useState("All");

    // 3) Direction
    const directionOptions = ["All", "In", "Out"];
    const [selectedDirection, setSelectedDirection] = useState("All");

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) return;

        (async () => {
            setIsFetching(true);
            try {
                const token = await getAccessTokenSilently();
                const resp = await getCTXRlistGlobal(-1, 1, token);
                const dataArray: CtxrItem[] = resp.data || [];

                setFullCTXRlist(dataArray);
                const mapped = dataArray.map((item) => `${item.name} (${item.description})`);
                setFirstOptions(mapped);

                if (mapped.length > 0) {
                    setSelectedFirst(mapped[0]);
                }
            } catch (err) {
                console.error("Error =>", err);
            } finally {
                setIsFetching(false);
            }
        })();
    }, [isLoading, isAuthenticated, getAccessTokenSilently]);

    // Al cambiar CTX-R => neighbors
    const handleSelectFirst = async (val: string) => {
        setSelectedFirst(val);
        setSelectedSecond("All");
        setSecondOptions([]);
        setSelectedDirection("All");

        try {
            setIsFetching(true);
            const foundObj = fullCTXRlist.find(
                (obj) => `${obj.name} (${obj.description})` === val
            );
            if (!foundObj) {
                setSecondOptions(["All"]);
                return;
            }

            const token = await getAccessTokenSilently();
            const resp = await getNeighborsListByCtxrId(token, foundObj.id, 1);
            const rawNeighbors = resp.data as Array<{ neighbor: string }>;

            if (!rawNeighbors.length) {
                setSecondOptions(["All"]);
            } else {
                const neighborsList = ["All", ...rawNeighbors.map((n) => n.neighbor)];
                setSecondOptions(neighborsList);
            }
        } catch (err) {
            console.error("Error =>", err);
        } finally {
            setIsFetching(false);
        }
    };

    const handleSelectSecond = (val: string) => setSelectedSecond(val);
    const handleSelectDirection = (val: string) => setSelectedDirection(val);

    // Botón Refresh => POST => set data
    const handleRefresh = async () => {
        try {
            setIsFetching(true);
            const foundObj = fullCTXRlist.find(
                (obj) => `${obj.name} (${obj.description})` === selectedFirst
            );
            if (!foundObj) {
                console.warn("No CTXR found for", selectedFirst);
                return;
            }

            const neighbor = selectedSecond === "All" ? "" : selectedSecond;
            const type = selectedDirection.toLowerCase();

            const token = await getAccessTokenSilently();
            const resp = await postBgpSummary(token, foundObj.id, 1, {
                neighbor,
                type,
            });
            console.log("POST =>", resp.data);

            // Mapeamos a ExtendedBgpTables
            const shaped: ExtendedBgpTables[] = (resp.data as BgpSummaryItem[]).map(
                (item) => ({
                    prefix: item.nlri || "",
                    family: item.family || "",
                    neighbor: item.neighbor || "",
                    localPref: item.localpreference || "",
                    community: item.community || "",
                    extendedCommunity: item.extendedcommunity || "",
                    direction: item.type || "",

                    routerId: item.routerid || "",
                    asPath: item.aspath || "",
                    localIp: item.localip || "",
                    localAs: item.localas || "",
                    peerAs: item.peeras || "",
                    subnet: item.subnet || "",
                    route_table: item.route_table || "",
                    // etc.
                })
            );
            setTablesData(shaped);
        } catch (err) {
            console.error("Error =>", err);
            alert("Error => ver consola");
        } finally {
            setIsFetching(false);
        }
    };

    // Filtro local
    const filteredData = tablesData.filter((row) => {
        const term = searchTerm.toLowerCase();
        if (!term) return true;
        const includes = (val: string) => val.toLowerCase().includes(term);

        switch (searchColumn) {
            case "Prefix":
                return includes(row.prefix);
            case "Family":
                return includes(row.family);
            case "Neighbour":
                return includes(row.neighbor);
            case "Local preference":
                return includes(row.localPref);
            case "Community":
                return includes(row.community);
            case "Extended community":
                return includes(row.extendedCommunity);
            case "Direction":
                return includes(row.direction);
            case "All":
            default:
                return (
                    includes(row.prefix) ||
                    includes(row.family) ||
                    includes(row.neighbor) ||
                    includes(row.localPref) ||
                    includes(row.community) ||
                    includes(row.extendedCommunity) ||
                    includes(row.direction)
                );
        }
    });

    // Fila expandida en 2 columnas (estilo Figma)
    const renderExpanded = (row: ExtendedBgpTables) => {
        return (
            <div className="p-4 grid grid-cols-2 gap-4">
                {/* Primera columna */}
                <div
                    className="
            flex flex-col
            p-4
            space-y-2
            bg-semantic-bg-quaternary
            rounded-md
            divide-y divide-semantic-border-tertiary
          "
                >
                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Prefix:</span>
                        <span>{row.prefix}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Direction:</span>
                        <span>{row.direction}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Neighbour:</span>
                        <span>{row.neighbor}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Local IP:</span>
                        <span>{row.localIp || "—"}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Local AS:</span>
                        <span>{row.localAs || "—"}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Peer AS:</span>
                        <span>{row.peerAs || "—"}</span>
                    </div>
                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Route Table:</span>
                        <span>{row.route_table || "—"}</span>
                    </div>
                </div>

                {/* Segunda columna */}
                <div
                    className="
            flex flex-col
            p-4
            space-y-2
            bg-semantic-bg-quaternary
            rounded-md
            divide-y divide-semantic-border-tertiary
          "
                >
                    {/* Ejemplo de “label a la izquierda, valor a la derecha” */}
                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Router ID:</span>
                        <span>{row.routerId || "—"}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">AS Path:</span>
                        <span>{row.asPath || "—"}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Family:</span>
                        <span>{row.family}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Local Pref:</span>
                        <span>{row.localPref}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Community:</span>
                        <span>{row.community}</span>
                    </div>

                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Ext. Community:</span>
                        <span>{row.extendedCommunity || "—"}</span>
                    </div>
                    <div className="py-1 flex justify-between">
                        <span className="font-semibold">Subnet:</span>
                        <span>{row.subnet || "—"}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Aquí definimos el *botón personalizado* para expandir/cerrar.
    const renderExpandToggle = (
        isExpanded: boolean,
        onToggle: () => void,
        row: ExtendedBgpTables
    ) => {
        void row;
        return (
            <button
                onClick={() => {
                    console.log("[renderExpandToggle -> onClick]");
                    onToggle();
                }}
                className="
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-primary
                text-semantic-fg-secondary
                hover:bg-semantic-state-active
                w-10 h-10
                flex items-center justify-center
              "
            >
                {isExpanded ? (
                    <IconClosed className="w-4 h-4" />
                ) : (
                    <IconStar className="w-4 h-4" />
                )}
            </button>
        );
    };

    return (
        <div className="relative min-h-[500px]">
            {/* Encabezado con combos + Refresh */}
            <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm">
                <div className="flex items-end justify-between">
                    <div className="flex items-end space-x-4">
                        {/* CTX-R */}
                        <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                                CTX-R
                            </label>
                            <GenericDropdown
                                items={firstOptions}
                                value={selectedFirst}
                                onSelect={handleSelectFirst}
                                minWidthClass="min-w-[300px]"
                                widthClass="w-60"
                                defaultLabel="-- Select instance --"
                            />
                        </div>

                        {/* Neighbor */}
                        <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                                Neighbor
                            </label>
                            <GenericDropdown
                                items={secondOptions}
                                value={selectedSecond}
                                onSelect={handleSelectSecond}
                                widthClass="w-60"
                                defaultLabel="All"
                            />
                        </div>

                        {/* Direction */}
                        <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                                Direction
                            </label>
                            <GenericDropdown
                                items={directionOptions}
                                value={selectedDirection}
                                onSelect={handleSelectDirection}
                                widthClass="w-60"
                                defaultLabel="All"
                            />
                        </div>

                        {/* Refresh => POST => setTablesData */}
                        <GenericButton
                            label="Refresh"
                            variant="secondary"
                            shape="ghost"
                            size="md"
                            onClick={handleRefresh}
                        />
                    </div>
                </div>
            </div>

            {/* Filtro local */}
            <div className="mb-4 bg-[var(--semantic-bg-primary)]">
                <div className="flex items-end justify-between">
                    <div className="flex items-end space-x-4" />
                    <div className="flex items-center space-x-4">
                        <GenericFilter
                            searchColumn={searchColumn}
                            onSearchColumnChange={setSearchColumn}
                            searchTerm={searchTerm}
                            onSearchTermChange={setSearchTerm}
                            columnOptions={[
                                "All",
                                "Prefix",
                                "Family",
                                "Neighbour",
                                "Local preference",
                                "Community",
                                "Extended community",
                                "Direction",
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Tabla => 7 columnas, expandible => renderExpanded */}
            <GenericTable<ExtendedBgpTables>
                columns={tablesColumns}
                data={filteredData}
                initialRowsPerPage={5}
                rowsPerPageOptions={[5, 10, 20]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"

                // Subfila de 2 col
                expandable
                renderExpanded={renderExpanded}

                // Aquí pasamos nuestro botón custom:
                renderExpandToggle={renderExpandToggle}
            />

            {/* Overlay spinner */}
            {isFetching && (
                <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
                    <h2 className="text-xl font-semibold mb-4 text-white">
                        Processing request...
                    </h2>
                    <div
                        className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4 border-gray-300
              border-t-blue-500
            "
                    />
                </div>
            )}
        </div>
    );
}

export default BgpTablesPage;
